// services/analyticsService.js
import { getAnalytics, logEvent } from 'firebase/analytics'
import { app } from '../firebase'

class AnalyticsService {
  constructor () {
    this.analytics = null
    this.sessionId = this.generateSessionId()
    this.pageLoadTime = Date.now()
    this.errorBuffer = []

    // Analytics nur im Browser initialisieren
    if (typeof window !== 'undefined') {
      try {
        this.analytics = getAnalytics(app)
      } catch (error) {
        console.warn('Analytics nicht verfügbar:', error)
      }
    }
  }

  generateSessionId () {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  // ===== USER EVENTS =====

  trackPageView (pageName, additionalData = {}) {
    this.logEvent('page_view', {
      page_name: pageName,
      page_path: window.location.pathname,
      session_id: this.sessionId,
      ...additionalData
    })
  }

  trackUserAction (actionName, metadata = {}) {
    this.logEvent('user_action', {
      action: actionName,
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      ...metadata
    })
  }

  // ===== SEARCH & CHAT ANALYTICS =====

  trackSearch (query, deviceName, results) {
    this.logEvent('search_performed', {
      query_length: query.length,
      device_name: deviceName,
      result_count: results?.length || 0,
      has_error_code: /[EF]-?\d{1,5}/.test(query),
      session_id: this.sessionId
    })
  }

  trackChatMessage (messageType, contentLength, metadata = {}) {
    this.logEvent('chat_message', {
      message_type: messageType, // 'user' | 'bot'
      content_length: contentLength,
      device_name: metadata.deviceName,
      response_time_ms: metadata.responseTime,
      sources_count: metadata.sourcesCount || 0,
      has_sources: (metadata.sourcesCount || 0) > 0,
      session_id: this.sessionId
    })
  }

  trackChatPerformance (performanceData) {
    this.logEvent('chat_performance', {
      response_time: performanceData.responseTime,
      search_results: performanceData.searchResults,
      total_time: performanceData.totalTime,
      embedding_time: performanceData.embeddingTime,
      llm_time: performanceData.llmTime,
      session_id: this.sessionId
    })

    // Performance Warnung bei langsamen Responses
    if (performanceData.responseTime > 5000) {
      this.trackError('slow_response', {
        response_time: performanceData.responseTime,
        type: 'performance_warning'
      })
    }
  }

  // ===== CONTRIBUTION TRACKING =====

  trackContribution (action, metadata = {}) {
    this.logEvent('contribution_action', {
      action, // 'started' | 'step_completed' | 'submitted' | 'abandoned'
      current_step: metadata.currentStep,
      device_name: metadata.deviceName,
      category: metadata.category,
      difficulty: metadata.difficulty,
      session_id: this.sessionId
    })
  }

  trackContributionFunnel (step, completed = true) {
    this.logEvent('contribution_funnel', {
      step_number: step,
      step_completed: completed,
      session_id: this.sessionId
    })
  }

  // ===== MODERATION TRACKING =====

  trackModeration (action, knowledgeId, metadata = {}) {
    this.logEvent('moderation_action', {
      action, // 'approved' | 'rejected' | 'viewed'
      knowledge_id: knowledgeId,
      category: metadata.category,
      has_error_code: metadata.hasErrorCode || false,
      session_id: this.sessionId
    })
  }

  // ===== ENGAGEMENT TRACKING =====

  trackEngagement (eventName, data = {}) {
    this.logEvent('user_engagement', {
      engagement_type: eventName,
      duration_seconds: data.duration,
      interaction_count: data.interactionCount,
      session_id: this.sessionId
    })
  }

  trackTimeOnPage (pageName, duration) {
    this.logEvent('time_on_page', {
      page_name: pageName,
      duration_seconds: Math.round(duration / 1000),
      session_id: this.sessionId
    })
  }

  trackBounce (pageName, timeBeforeBounce) {
    if (timeBeforeBounce < 5000) {
      this.logEvent('bounce', {
        page_name: pageName,
        time_before_bounce_ms: timeBeforeBounce,
        session_id: this.sessionId
      })
    }
  }

  // ===== ERROR TRACKING =====

  trackError (errorType, errorDetails = {}) {
    const errorData = {
      error_type: errorType,
      error_message: errorDetails.message || 'Unknown error',
      error_stack: errorDetails.stack?.substring(0, 500),
      page_path: window.location.pathname,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      ...errorDetails
    }

    // In Buffer speichern für Batch-Upload
    this.errorBuffer.push(errorData)

    this.logEvent('error_occurred', errorData)

    // Critical Errors sofort loggen
    if (errorDetails.severity === 'critical') {
      this.flushErrorBuffer()
    }
  }

  trackNetworkError (url, statusCode, errorMessage) {
    this.trackError('network_error', {
      url,
      status_code: statusCode,
      error_message: errorMessage,
      severity: statusCode >= 500 ? 'critical' : 'warning'
    })
  }

  flushErrorBuffer () {
    if (this.errorBuffer.length === 0) return

    // Sammle alle Errors
    const errors = [...this.errorBuffer]
    this.errorBuffer = []

    // Batch-Event
    this.logEvent('error_batch', {
      error_count: errors.length,
      errors: JSON.stringify(errors),
      session_id: this.sessionId
    })
  }

  // ===== CONVERSION TRACKING =====

  trackConversion (conversionType, value = 0) {
    this.logEvent('conversion', {
      conversion_type: conversionType,
      value,
      session_id: this.sessionId
    })
  }

  // ===== FEATURE USAGE =====

  trackFeatureUsage (featureName, metadata = {}) {
    this.logEvent('feature_used', {
      feature_name: featureName,
      ...metadata,
      session_id: this.sessionId
    })
  }

  // ===== A/B TESTING =====

  trackExperiment (experimentName, variant) {
    this.logEvent('experiment_variant', {
      experiment_name: experimentName,
      variant,
      session_id: this.sessionId
    })
  }

  // ===== HELPER =====

  logEvent (eventName, parameters = {}) {
    if (!this.analytics) {
      console.log('📊 Analytics Event:', eventName, parameters)
      return
    }

    try {
      // Entferne undefined/null Werte
      const cleanParams = Object.entries(parameters).reduce(
        (acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = value
          }
          return acc
        },
        {}
      )

      logEvent(this.analytics, eventName, cleanParams)
    } catch (error) {
      console.error('Analytics Error:', error)
    }
  }

  // ===== SESSION MANAGEMENT =====

  startSession () {
    this.sessionId = this.generateSessionId()
    this.pageLoadTime = Date.now()

    this.logEvent('session_start', {
      session_id: this.sessionId,
      referrer: document.referrer,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`
    })
  }

  endSession () {
    const sessionDuration = Date.now() - this.pageLoadTime

    this.logEvent('session_end', {
      session_id: this.sessionId,
      duration_seconds: Math.round(sessionDuration / 1000)
    })

    // Flush errors before ending
    this.flushErrorBuffer()
  }
}

// Singleton Export
const analyticsService = new AnalyticsService()

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    analyticsService.endSession()
  })
}

export default analyticsService
